/* eslint-disable import/no-extraneous-dependencies */
import React, { useMemo, useCallback } from 'react';
import ReactFlow, {
    Background,
    Controls,
    MiniMap,
    Handle,
    Position,
    Node,
    Edge,
    MarkerType,
    useNodesState,
    useEdgesState,
    BackgroundVariant,
    ReactFlowProvider,
    useReactFlow
} from 'reactflow';
import 'reactflow/dist/style.css';
import { Box, Typography, Paper, useTheme, keyframes, alpha, Chip, Stack, Tooltip, LinearProgress, IconButton } from '@mui/material';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import PendingIcon from '@mui/icons-material/Pending';
import WebhookIcon from '@mui/icons-material/Webhook';
import HourglassBottomIcon from '@mui/icons-material/HourglassBottom';
import FullscreenIcon from '@mui/icons-material/Fullscreen';
import FullscreenExitIcon from '@mui/icons-material/FullscreenExit';
import CloseIcon from '@mui/icons-material/Close';
import FilterCenterFocusIcon from '@mui/icons-material/FilterCenterFocus';
import AddIcon from '@mui/icons-material/Add';
import RemoveIcon from '@mui/icons-material/Remove';
// @ts-ignore
import dagre from 'dagre';

// Animations
const pulse = keyframes`
  0% { transform: scale(1); box-shadow: 0 0 0 0 rgba(33, 150, 243, 0.4); }
  70% { transform: scale(1.02); box-shadow: 0 0 0 15px rgba(33, 150, 243, 0); }
  100% { transform: scale(1); box-shadow: 0 0 0 0 rgba(33, 150, 243, 0); }
`;

const errorPulse = keyframes`
  0% { transform: scale(1); box-shadow: 0 0 0 0 rgba(244, 67, 54, 0.4); }
  70% { transform: scale(1.02); box-shadow: 0 0 0 20px rgba(244, 67, 54, 0); }
  100% { transform: scale(1); box-shadow: 0 0 0 0 rgba(244, 67, 54, 0); }
`;

const shake = keyframes`
  0%, 100% { transform: translateX(0); }
  25% { transform: translateX(-4px); }
  75% { transform: translateX(4px); }
`;

const waitingPulse = keyframes`
  0% { transform: scale(1); box-shadow: 0 0 0 0 rgba(255, 193, 7, 0.4); }
  70% { transform: scale(1.02); box-shadow: 0 0 0 15px rgba(255, 193, 7, 0); }
  100% { transform: scale(1); box-shadow: 0 0 0 0 rgba(255, 193, 7, 0); }
`;

const spin = keyframes`
  from { transform: rotate(0deg); }
  to { transform: rotate(360deg); }
`;

// Custom Node Component
const ProcessNode = ({ data }: { data: any }) => {
    const theme = useTheme();
    const { name, type, status, color, error } = data;
    const isRunning = status === 'RUNNING' || status === 'WAITING_FOR_WEBHOOK' || status === 'DELAYING';
    const isFailed = status === 'FAILED';
    const isWaiting = status === 'WAITING_FOR_WEBHOOK';
    const isDelay = status === 'DELAYING';

    let label = type;
    if (isWaiting) {
        label = 'WAITING';
    } else if (isDelay) {
        label = 'DELAY';
    }

    let displayStatus = status;
    if (status === 'WAITING_FOR_WEBHOOK') {
        displayStatus = 'WEBHOOK';
    } else if (status === 'DELAYING') {
        displayStatus = 'TIMER';
    } else if (status === 'SUCCEEDED_WITH_RETRIES') {
        displayStatus = 'RETRIED';
    }

    const getIcon = () => {
        switch (status) {
            case 'SUCCEEDED':
            case 'SUCCEEDED_WITH_RETRIES':
                return <CheckCircleOutlineIcon sx={{ fontSize: 20, color: 'success.light' }} />;
            case 'FAILED':
                return <ErrorOutlineIcon sx={{ fontSize: 20, color: 'error.light' }} />;
            case 'RUNNING':
                return <PendingIcon sx={{ fontSize: 20, color: 'primary.light', animation: `${spin} 2s linear infinite` }} />;
            case 'WAITING_FOR_WEBHOOK':
                return <WebhookIcon sx={{ fontSize: 20, color: 'warning.light', animation: `${pulse} 2s infinite ease-in-out` }} />;
            case 'DELAYING':
                return <HourglassBottomIcon sx={{ fontSize: 20, color: 'primary.light', animation: `${pulse} 2s infinite ease-in-out` }} />;
            default:
                return null;
        }
    };

    const getNodeAnimation = () => {
        if (isFailed) return `${shake} 0.5s ease-in-out 1, ${errorPulse} 2s infinite ease-in-out`;
        if (isWaiting) return `${waitingPulse} 2s infinite ease-in-out`;
        if (isRunning) return `${pulse} 2s infinite ease-in-out`;
        return 'none';
    };

    const getBoxShadow = () => {
        if (isRunning) return `0 0 30px ${alpha(color, 0.3)}`;
        if (isFailed) return `0 0 40px ${alpha(color, 0.4)}`;
        if (status === 'SUCCEEDED' || status === 'SUCCEEDED_WITH_RETRIES') return `0 0 20px ${alpha(color, 0.15)}`;
        return `0 8px 32px ${alpha(theme.palette.common.black, 0.2)}`;
    };

    return (
        <Paper
            elevation={isRunning || isFailed ? 12 : 2}
            sx={{
                p: 2.5,
                width: 190,
                background:
                    theme.palette.mode === 'dark'
                        ? `linear-gradient(135deg, ${alpha(theme.palette.background.paper, 0.9)} 0%, ${alpha(
                              theme.palette.background.paper,
                              0.7
                          )} 100%)`
                        : `linear-gradient(135deg, #fff 0%, ${alpha(theme.palette.grey[100], 0.8)} 100%)`,
                border: `1px solid ${alpha(color, 0.4)}`,
                borderLeft: `6px solid ${color}`,
                borderRadius: '12px',
                backdropFilter: 'blur(12px)',
                transition: 'all 0.4s cubic-bezier(0.175, 0.885, 0.32, 1.275)',
                animation: getNodeAnimation(),
                boxShadow: getBoxShadow(),
                ...(isWaiting && {
                    backgroundImage: `linear-gradient(135deg, ${alpha(theme.palette.warning.dark, 0.15)} 0%, ${alpha(
                        theme.palette.warning.main,
                        0.05
                    )} 100%)`,
                    borderWidth: '2px',
                    borderColor: theme.palette.warning.main
                }),
                '&:hover': {
                    transform: 'translateY(-8px) scale(1.02)',
                    boxShadow: `0 15px 40px ${alpha(color, 0.25)}`,
                    borderColor: color
                }
            }}
        >
            <Handle
                type="target"
                position={Position.Left}
                id="left"
                style={{ background: color, width: 8, height: 8, border: 'none', opacity: 0 }}
            />
            <Handle
                type="target"
                position={Position.Right}
                id="right"
                style={{ background: color, width: 8, height: 8, border: 'none', opacity: 0 }}
            />
            <Handle
                type="target"
                position={Position.Top}
                id="top"
                style={{ background: color, width: 8, height: 8, border: 'none', opacity: 0 }}
            />
            <Handle
                type="target"
                position={Position.Bottom}
                id="bottom"
                style={{ background: color, width: 8, height: 8, border: 'none', opacity: 0 }}
            />
            <Stack spacing={1.5}>
                <Stack direction="row" justifyContent="space-between" alignItems="flex-start">
                    <Typography
                        variant="h6"
                        sx={{
                            fontWeight: 900,
                            fontSize: '0.92rem',
                            color: theme.palette.text.primary,
                            letterSpacing: '-0.2px',
                            lineHeight: 1.2,
                            maxWidth: '90%',
                            display: '-webkit-box',
                            WebkitLineClamp: 2,
                            WebkitBoxOrient: 'vertical',
                            overflow: 'hidden',
                            wordBreak: 'break-word'
                        }}
                    >
                        {name}
                    </Typography>
                    {getIcon()}
                </Stack>
                <Stack direction="row" justifyContent="space-between" alignItems="center">
                    <Chip
                        label={label}
                        size="small"
                        sx={{
                            fontSize: '0.62rem',
                            height: '18px',
                            bgcolor: alpha(color, 0.12),
                            color: status !== 'PENDING' ? color : 'text.secondary',
                            fontWeight: 800,
                            border: `1px solid ${alpha(color, 0.2)}`,
                            textTransform: 'uppercase'
                        }}
                    />
                    {status !== 'PENDING' && !error && (
                        <Typography
                            variant="caption"
                            sx={{
                                fontSize: '0.6rem',
                                fontWeight: 800,
                                color: alpha(color, 0.9),
                                textTransform: 'uppercase',
                                bgcolor: alpha(color, 0.08),
                                px: 0.8,
                                borderRadius: 1
                            }}
                        >
                            {displayStatus}
                        </Typography>
                    )}
                </Stack>
                {data.type === 'Map' && data.mapProgress && (data.mapProgress.total > 0 || data.mapProgress.isFinished) && (
                    <Box sx={{ mt: 1.5, width: '100%' }}>
                        <Stack direction="row" justifyContent="space-between" alignItems="center" mb={0.5}>
                            <Typography
                                variant="caption"
                                sx={{
                                    fontSize: '0.62rem',
                                    fontWeight: 900,
                                    color: data.mapProgress.failed > 0 ? 'error.main' : 'text.secondary',
                                    letterSpacing: '0.4px',
                                    textTransform: 'uppercase'
                                }}
                            >
                                {(() => {
                                    if (data.mapProgress.failed > 0) return `${data.mapProgress.failed} FAILURES`;
                                    if (data.mapProgress.total === 0 && data.mapProgress.isFinished) return 'EMPTY DATASET';
                                    return 'ITERATIONS';
                                })()}
                            </Typography>
                            <Typography
                                variant="caption"
                                sx={{
                                    fontSize: '0.65rem',
                                    fontWeight: 900,
                                    color: data.mapProgress.failed > 0 ? 'error.main' : theme.palette.primary.main
                                }}
                            >
                                {data.mapProgress.total === 0 ? '0/0' : `${data.mapProgress.succeeded}/${data.mapProgress.total}`}
                            </Typography>
                        </Stack>
                        <LinearProgress
                            variant="determinate"
                            value={data.mapProgress.percentage}
                            sx={{
                                height: 6,
                                borderRadius: 3,
                                bgcolor: alpha(data.mapProgress.failed > 0 ? theme.palette.error.main : theme.palette.primary.main, 0.1),
                                '& .MuiLinearProgress-bar': {
                                    borderRadius: 3,
                                    background: data.mapProgress.failed > 0 ? theme.palette.error.main : theme.palette.primary.main
                                }
                            }}
                        />
                    </Box>
                )}
                {error && (
                    <Typography
                        variant="caption"
                        sx={{
                            color: 'error.main',
                            fontWeight: 700,
                            fontSize: '0.65rem',
                            lineHeight: 1.1,
                            mt: 0.5,
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            display: '-webkit-box',
                            WebkitLineClamp: 2,
                            WebkitBoxOrient: 'vertical'
                        }}
                    >
                        <Tooltip title={error.cause || error.error || 'Unknown Error'}>
                            <span>{error.error || 'Error'}</span>
                        </Tooltip>
                    </Typography>
                )}
            </Stack>
            <Handle
                type="source"
                position={Position.Left}
                id="left-out"
                style={{ background: color, width: 8, height: 8, border: 'none', opacity: 0 }}
            />
            <Handle
                type="source"
                position={Position.Right}
                id="right-out"
                style={{ background: color, width: 8, height: 8, border: 'none', opacity: 0 }}
            />
            <Handle
                type="source"
                position={Position.Top}
                id="top-out"
                style={{ background: color, width: 8, height: 8, border: 'none', opacity: 0 }}
            />
            <Handle
                type="source"
                position={Position.Bottom}
                id="bottom-out"
                style={{ background: color, width: 8, height: 8, border: 'none', opacity: 0 }}
            />
        </Paper>
    );
};

// Custom Group Background Node
const GroupBackgroundNode = ({ data }: { data: any }) => {
    const theme = useTheme();
    return (
        <Box
            sx={{
                width: '100%',
                height: '100%',
                bgcolor: alpha(data.color || theme.palette.primary.main, 0.02),
                borderRadius: '16px',
                border: `2px dashed ${alpha(data.color || theme.palette.primary.main, 0.25)}`,
                pointerEvents: 'none',
                position: 'relative',
                transition: 'all 0.3s ease',
                '&::before': {
                    content: '""',
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    right: 0,
                    height: '55px',
                    bgcolor: alpha(data.color || theme.palette.primary.main, 0.04),
                    borderRadius: '14px 14px 0 0',
                    borderBottom: `1px solid ${alpha(data.color || theme.palette.primary.main, 0.08)}`
                }
            }}
        >
            <Handle
                type="source"
                position={Position.Right}
                id="right"
                style={{ top: '50%', right: -2, opacity: 0, pointerEvents: 'none' }}
            />
        </Box>
    );
};

const nodeTypes = {
    process: ProcessNode,
    groupBackground: GroupBackgroundNode
};

interface Props {
    definition: any;
    executionHistoryData: any[];
    executionStatus?: string;
}

const StateMachineFlowGraph = ({ definition, executionHistoryData, executionStatus }: Props) => {
    const theme = useTheme();
    const [isFullScreen, setIsFullScreen] = React.useState(false);

    const toggleFullScreen = () => {
        setIsFullScreen(!isFullScreen);
    };

    // Logic to layout nodes in a "Snake" or "Wrapped" pattern for better space utilization
    const getLayoutedElements = useCallback(
        (nodes: Node[], edges: Edge[]) => {
            const dagreGraph = new dagre.graphlib.Graph({ compound: true });
            dagreGraph.setDefaultNodeLabel(() => ({}));
            dagreGraph.setDefaultEdgeLabel(() => ({}));

            const COLUMNS = 5;
            const nodeWidth = 190;
            const nodeHeight = 150;

            dagreGraph.setGraph({
                rankdir: 'LR',
                ranksep: 120,
                nodesep: 100,
                ranker: 'network-simplex',
                marginx: 40,
                marginy: 40
            });

            // Identify hierarchy
            const parentMap = new Map();
            nodes.forEach((node) => {
                if (node.data.groupId) {
                    parentMap.set(node.id, node.data.groupId);
                }
            });

            const uniqueParents = new Set(parentMap.values());

            // 1. Register all nodes first
            nodes.forEach((node) => {
                dagreGraph.setNode(node.id, { width: nodeWidth, height: nodeHeight });
            });

            // 2. Register all clusters
            uniqueParents.forEach((pId: any) => {
                dagreGraph.setNode(`cluster-${pId}`, { label: pId, isCluster: true });
            });

            // 3. Establish relationships
            nodes.forEach((node) => {
                const clusterId = `cluster-${node.data.groupId}`;
                if (node.data.groupId && dagreGraph.hasNode(clusterId)) {
                    dagreGraph.setParent(node.id, clusterId);
                }

                // FUSE: If this node IS the container, it also belongs inside its own cluster
                // This makes the Map node appear as the "header" of the box
                if (node.data.isContainer) {
                    const selfClusterId = `cluster-${node.id}`;
                    if (dagreGraph.hasNode(selfClusterId)) {
                        dagreGraph.setParent(node.id, selfClusterId);
                    }
                }
            });

            uniqueParents.forEach((pId: any) => {
                const grandParentId = parentMap.get(pId);
                const clusterId = `cluster-${pId}`;
                const parentClusterId = `cluster-${grandParentId}`;
                if (grandParentId && dagreGraph.hasNode(clusterId) && dagreGraph.hasNode(parentClusterId)) {
                    dagreGraph.setParent(clusterId, parentClusterId);
                }
            });

            // 4. Add edges
            edges.forEach((edge) => {
                if (dagreGraph.hasNode(edge.source) && dagreGraph.hasNode(edge.target)) {
                    dagreGraph.setEdge(edge.source, edge.target);
                }
            });

            const getAncestors = (nodeId: string) => {
                const ancestors = [];
                let curr = parentMap.get(nodeId);
                while (curr) {
                    ancestors.push(curr);
                    curr = parentMap.get(curr);
                }
                return ancestors;
            };

            // 5. Refine sequence: Force ALL nodes inside a container to finish before the parent's Next node
            // We use individual edges instead of cluster edges to avoid Dagre ranker crashes
            nodes.forEach((node) => {
                const ancestors = getAncestors(node.id);
                ancestors.forEach((gId) => {
                    const parentNode = nodes.find((n) => n.id === gId);
                    if (parentNode && parentNode.data.next && dagreGraph.hasNode(parentNode.data.next)) {
                        // Create a hidden layout edge from this internal node to the successor task
                        // This forces the successor to be in a higher rank than every internal task
                        dagreGraph.setEdge(node.id, parentNode.data.next, { weight: 1, minlen: 1 });
                    }
                });
            });

            dagre.layout(dagreGraph);

            const layoutedNodes = nodes.map((node) => {
                const nodeWithPosition = dagreGraph.node(node.id);

                return {
                    ...node,
                    targetPosition: Position.Left,
                    sourcePosition: Position.Right,
                    position: {
                        x: nodeWithPosition.x - nodeWidth / 2,
                        y: nodeWithPosition.y - nodeHeight / 2
                    }
                };
            });

            // Calculate bounding boxes for groups to create visual containers
            const groups: Record<string, { minX: number; minY: number; maxX: number; maxY: number; color: string; label: string }> = {};

            layoutedNodes.forEach((node) => {
                const x = node.position.x;
                const y = node.position.y;
                const ancestors = getAncestors(node.id);

                // FUSE: Include the container node itself in its own group box calculation
                if (node.data.isContainer) {
                    ancestors.unshift(node.id);
                }

                ancestors.forEach((gId) => {
                    if (!groups[gId]) {
                        const parentNode = layoutedNodes.find((n) => n.id === gId);
                        groups[gId] = {
                            minX: x,
                            minY: y,
                            maxX: x + nodeWidth,
                            maxY: y + nodeHeight,
                            color: parentNode?.data.color || theme.palette.primary.main,
                            label: parentNode?.data.name || gId
                        };
                    } else {
                        groups[gId].minX = Math.min(groups[gId].minX, x);
                        groups[gId].minY = Math.min(groups[gId].minY, y);
                        groups[gId].maxX = Math.max(groups[gId].maxX, x + nodeWidth);
                        groups[gId].maxY = Math.max(groups[gId].maxY, y + nodeHeight);
                    }
                });
            });

            const groupNodes = Object.entries(groups)
                .map(([id, box]) => ({
                    id: `group-bg-${id}`,
                    type: 'groupBackground',
                    data: { label: box.label, color: box.color },
                    position: { x: box.minX - 35, y: box.minY - 25 },
                    style: { width: box.maxX - box.minX + 70, height: box.maxY - box.minY + 50 },
                    zIndex: -1,
                    draggable: false,
                    selectable: false,
                    area: (box.maxX - box.minX + 70) * (box.maxY - box.minY + 50)
                }))
                .sort((a, b) => b.area - a.area); // Draw largest (outermost) groups first so they stay in the background

            const layoutedEdges = edges.map((edge) => {
                const sourceNode = layoutedNodes.find((n) => n.id === edge.source);
                const targetNode = layoutedNodes.find((n) => n.id === edge.target);

                if (!sourceNode || !targetNode) return edge;

                // FUSE: If the source is a container and the edge goes to its successor,
                // redirect it to start from the right edge of the container box
                if (sourceNode.data.isContainer && edge.target === sourceNode.data.next) {
                    return {
                        ...edge,
                        source: `group-bg-${edge.source}`,
                        sourceHandle: 'right'
                    };
                }

                const dx = targetNode.position.x - sourceNode.position.x;
                const dy = targetNode.position.y - sourceNode.position.y;

                let sourceHandle = 'right-out';
                let targetHandle = 'left';
                let edgeType = 'default'; // Elegantly smooth bezier curves for forward paths

                // Lookbacks / Loops
                if (dx < -10) {
                    sourceHandle = 'top-out';
                    targetHandle = 'top';
                    edgeType = 'bezier'; // Loops use a sweeping curve to stay out of the way
                }

                const isPathActive =
                    (sourceNode.data.status === 'SUCCEEDED' ||
                        sourceNode.data.status === 'SUCCEEDED_WITH_RETRIES' ||
                        sourceNode.data.status === 'RUNNING' ||
                        sourceNode.data.status === 'WAITING_FOR_WEBHOOK' ||
                        sourceNode.data.status === 'DELAYING') &&
                    targetNode.data.status !== 'PENDING';

                const isRunning = sourceNode.data.status === 'RUNNING';
                const isWaiting = sourceNode.data.status === 'WAITING_FOR_WEBHOOK';
                const isDelay = sourceNode.data.status === 'DELAYING';

                // Accurately show animation on the edge connecting to a currently running state
                const isNextRunning =
                    targetNode.data.status === 'RUNNING' ||
                    targetNode.data.status === 'WAITING_FOR_WEBHOOK' ||
                    targetNode.data.status === 'DELAYING';
                const isAnimated = isRunning || isWaiting || isDelay || (isPathActive && isNextRunning);

                const color = sourceNode.data.color;

                return {
                    ...edge,
                    sourceHandle,
                    targetHandle,
                    type: edgeType,
                    animated: isAnimated,
                    interactionWidth: 20,
                    pathOptions: { borderRadius: 24, offset: 5 }, // Smoother, sleeker curves on corners
                    style: {
                        ...edge.style,
                        stroke: isPathActive ? color : theme.palette.divider,
                        strokeWidth: isPathActive ? 3.5 : 1.5,
                        filter: isPathActive ? `drop-shadow(0 0 8px ${alpha(color, 0.6)})` : 'none',
                        transition: 'stroke 0.4s ease, opacity 0.4s ease, filter 0.4s ease',
                        opacity: isPathActive ? 1 : 0.45,
                        strokeDasharray: edgeType === 'bezier' ? '6,6' : 'none'
                    },
                    markerEnd: {
                        type: MarkerType.ArrowClosed,
                        color: isPathActive ? color : theme.palette.divider,
                        width: 15,
                        height: 15,
                        strokeWidth: 1
                    }
                };
            });

            return { nodes: [...groupNodes, ...layoutedNodes], edges: layoutedEdges };
        },
        [definition, theme]
    );

    // Helper to flatten the hierarchical history data for easy lookup
    const flattenedHistory = React.useMemo(() => {
        const flat: any[] = [];
        const flatten = (items: any[]) => {
            items?.forEach((item) => {
                flat.push(item);
                if (item.children && item.children.length > 0) {
                    flatten(item.children);
                }
            });
        };
        flatten(executionHistoryData);
        return flat;
    }, [executionHistoryData]);

    const getStateStatus = useCallback(
        (stateName: string, groupId?: string) => {
            // Match exactly or by iteration pattern (e.g., "StateName (1)") for Map internal states
            // Use flattenedHistory to ensure we find nested tasks
            const historyRow = flattenedHistory?.find((h) => h.name === stateName || (groupId && h.name.startsWith(`${stateName} `)));

            if (!historyRow) return 'PENDING';

            const events = historyRow.events || [];
            const stateDef = definition.States[stateName];

            // 1. Success (overrides everything if finished)
            const hasSuccessEvent =
                historyRow?.mapProgress?.isFinished || events.some((e: any) => e.type.includes('Exited') || e.type.includes('Succeeded'));
            const isWaitFinished = stateDef?.Type === 'Wait' && events.length >= 2;
            const hasPriorFailures = events.some(
                (e: any) => e.type.includes('Failed') || e.type.includes('Aborted') || e.type.includes('TimedOut')
            );

            // If execution is succeeded and we have events, it must have finished
            const isFinishedByExecution = executionStatus === 'SUCCEEDED' && events.length > 0 && !historyRow.error;

            if (hasSuccessEvent || isWaitFinished || isFinishedByExecution) {
                return hasPriorFailures && stateDef?.Type !== 'Wait' ? 'SUCCEEDED_WITH_RETRIES' : 'SUCCEEDED';
            }

            // 2. Failure (only if not succeeded)
            const hasFailedEvent =
                historyRow.error ||
                events.some((e: any) => e.type.includes('Failed') || e.type.includes('Aborted') || e.type.includes('TimedOut'));
            if (hasFailedEvent) return 'FAILED';

            // 3. Waiting (from backend logic)
            if (historyRow.isWaiting) {
                return stateDef?.Type === 'Wait' ? 'DELAYING' : 'WAITING_FOR_WEBHOOK';
            }

            // 4. Running/Pending
            return events.length > 0 ? 'RUNNING' : 'PENDING';
        },
        [executionHistoryData, definition]
    );

    const getStateError = useCallback(
        (stateName: string) => {
            const historyRow = executionHistoryData?.find((h) => h.name === stateName);
            return historyRow?.error;
        },
        [executionHistoryData]
    );

    const getMapProgress = useCallback(
        (stateName: string) => {
            const historyRow = executionHistoryData?.find((h) => h.name === stateName);
            return historyRow?.mapProgress;
        },
        [executionHistoryData]
    );

    const getStatusColor = useCallback(
        (status: string) => {
            switch (status) {
                case 'SUCCEEDED':
                case 'SUCCEEDED_WITH_RETRIES':
                    return theme.palette.success.main;
                case 'FAILED':
                    return theme.palette.error.main;
                case 'RUNNING':
                    return theme.palette.primary.main;
                case 'WAITING_FOR_WEBHOOK':
                    return theme.palette.warning.main;
                case 'DELAYING':
                    return theme.palette.primary.main;
                case 'PENDING':
                    return alpha(theme.palette.text.disabled, 0.3);
                default:
                    return alpha(theme.palette.text.disabled, 0.3);
            }
        },
        [theme]
    );

    const elements = useMemo(() => {
        if (!definition || !definition.States) return { nodes: [], edges: [] };

        const states = definition.States;
        const initialNodes: Node[] = [];
        const initialEdges: Edge[] = [];

        const addEdge = (sourceId: string, targetId: string, status: string, color: string, label?: string, isDash = false) => {
            const nextStatus = getStateStatus(targetId);
            const isActive =
                (status === 'SUCCEEDED' || status === 'RUNNING' || status === 'WAITING_FOR_WEBHOOK') && nextStatus !== 'PENDING';

            initialEdges.push({
                id: `${sourceId}-${targetId}-${label || 'path'}`,
                source: sourceId,
                target: targetId,
                label,
                animated: status === 'RUNNING' || status === 'WAITING_FOR_WEBHOOK',
                style: {
                    stroke: isActive ? color : alpha(theme.palette.text.disabled, 0.1),
                    strokeWidth: isActive ? 3 : 1.2,
                    strokeDasharray: isDash ? '6,4' : 'none',
                    opacity: isActive ? 1 : 0.3
                },
                markerEnd: {
                    type: MarkerType.ArrowClosed,
                    color: isActive ? color : alpha(theme.palette.text.disabled, 0.1),
                    width: 15,
                    height: 15
                }
            });
        };

        const processStates = (statesToProcess: any, groupId?: string) => {
            Object.entries(statesToProcess).forEach(([name, state]: [string, any]) => {
                const status = getStateStatus(name, groupId);
                const color = getStatusColor(status);

                // Prevent duplicate nodes
                if (!initialNodes.find((n) => n.id === name)) {
                    initialNodes.push({
                        id: name,
                        type: 'process',
                        data: {
                            name,
                            type: state.Type,
                            status,
                            color,
                            error: getStateError(name),
                            mapProgress: getMapProgress(name),
                            groupId,
                            isContainer: state.Type === 'Map' || state.Type === 'Parallel',
                            next: state.Next
                        },
                        position: { x: 0, y: 0 }
                    });
                }

                if (state.Next) {
                    addEdge(name, state.Next, status, color);
                }

                if (state.Choices) {
                    state.Choices.forEach((choice: any, i: number) => {
                        if (choice.Next) {
                            addEdge(name, choice.Next, status, color, choice.Label || (choice.Variable ? 'Choice' : undefined), true);
                        }
                    });
                    if (state.Default) {
                        addEdge(name, state.Default, status, color, 'Default', true);
                    }
                }

                if (state.Catch) {
                    state.Catch.forEach((catchBlock: any, i: number) => {
                        if (catchBlock.Next) {
                            addEdge(name, catchBlock.Next, 'FAILED', theme.palette.error.main, 'Error', true);
                        }
                    });
                }

                // Recurse into Parallel branches
                if (state.Type === 'Parallel' && state.Branches) {
                    state.Branches.forEach((branch: any) => {
                        processStates(branch.States, name);
                        addEdge(name, branch.StartAt, status, color);
                    });
                }

                // Recurse into Map processors
                if (state.Type === 'Map' && (state.ItemProcessor || state.Iterator)) {
                    const processor = state.ItemProcessor || state.Iterator;
                    processStates(processor.States, name);
                    addEdge(name, processor.StartAt, status, color);
                }
            });
        };

        processStates(states);

        return getLayoutedElements(initialNodes, initialEdges);
    }, [definition, executionHistoryData, theme, getLayoutedElements, getStateError, getStateStatus, getStatusColor]);

    const [nodes, setNodes, onNodesChange] = useNodesState(elements.nodes);
    const [edges, setEdges, onEdgesChange] = useEdgesState(elements.edges);

    React.useEffect(() => {
        setNodes(elements.nodes);
        setEdges(elements.edges);
    }, [elements, setNodes, setEdges]);

    const GraphViewportManager = ({ currentNodes, historyData }: { currentNodes: Node[]; historyData: any[] }) => {
        const { setCenter, fitView } = useReactFlow();

        React.useEffect(() => {
            let timeoutId: NodeJS.Timeout;

            if (currentNodes.length > 0) {
                // Give the graph time to render before animating viewport
                timeoutId = setTimeout(() => {
                    // Priority 1: Currently running / waiting
                    let targetNode = currentNodes.find(
                        (n) => n.data.status === 'RUNNING' || n.data.status === 'WAITING_FOR_WEBHOOK' || n.data.status === 'DELAYING'
                    );

                    // Priority 2: Error / Failed
                    if (!targetNode) {
                        targetNode = currentNodes.find((n) => n.data.status === 'FAILED');
                    }

                    // Priority 3: Last successful node
                    if (!targetNode && historyData?.length) {
                        const sortedHistory = [...historyData].sort((a, b) => {
                            const aLast = a.events?.[a.events.length - 1]?.timestamp || 0;
                            const bLast = b.events?.[b.events.length - 1]?.timestamp || 0;
                            // Return valid integer values for standard TS sort
                            return new Date(bLast).getTime() - new Date(aLast).getTime();
                        });
                        const lastActiveName = sortedHistory[0]?.name;
                        if (lastActiveName) {
                            targetNode = currentNodes.find((n) => n.id === lastActiveName);
                        }
                    }

                    if (targetNode) {
                        const x = targetNode.position.x + 95; // Center of nodeWidth (190)
                        const y = targetNode.position.y + 55; // Center of nodeHeight (110)
                        setCenter(x, y, { zoom: 0.85, duration: 800 });
                    } else {
                        fitView({ padding: 0.3, duration: 800 });
                    }
                }, 100);
            } // Close if statement

            return () => {
                if (timeoutId) clearTimeout(timeoutId);
            };
        }, [currentNodes, historyData, setCenter, fitView]);

        return null;
    };

    const defaultBg = theme.palette.mode === 'dark' ? alpha(theme.palette.common.white, 0.02) : '#f8f9fa';
    const finalBg = isFullScreen ? theme.palette.background.default : defaultBg;

    const FlowHeader = () => (
        <>
            {isFullScreen && (
                <Tooltip title="Cerrar pantalla completa">
                    <IconButton
                        onClick={toggleFullScreen}
                        sx={{
                            position: 'absolute',
                            top: 20,
                            right: 20,
                            zIndex: 10001,
                            bgcolor: alpha(theme.palette.background.paper, 0.8),
                            backdropFilter: 'blur(10px)',
                            boxShadow: theme.shadows[4],
                            '&:hover': { bgcolor: theme.palette.background.paper }
                        }}
                    >
                        <CloseIcon />
                    </IconButton>
                </Tooltip>
            )}
            <Box
                sx={{
                    position: 'absolute',
                    top: 20,
                    left: 20,
                    zIndex: 10,
                    bgcolor: alpha(theme.palette.background.paper, 0.8),
                    backdropFilter: 'blur(10px)',
                    p: '8px 24px',
                    borderRadius: '50px',
                    border: `1px solid ${alpha(theme.palette.divider, 0.3)}`,
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1.5,
                    boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
                    transition: 'all 0.5s cubic-bezier(0.4, 0, 0.2, 1)',
                    cursor: 'pointer',
                    '&:hover': {
                        gap: 2.5,
                        '& .legend-stack': {
                            maxWidth: '1200px',
                            opacity: 1,
                            ml: 1,
                            pl: 2
                        }
                    }
                }}
            >
                <Typography variant="subtitle2" sx={{ fontWeight: 900, display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Box
                        component="span"
                        sx={{ width: 10, height: 10, borderRadius: '50%', bgcolor: 'primary.main', display: 'inline-block' }}
                    />
                    Execution Topology
                </Typography>
                <Chip
                    label="BETA"
                    size="small"
                    variant="filled"
                    color="primary"
                    sx={{ fontWeight: 900, height: 20, fontSize: '0.65rem' }}
                />
                <Stack
                    className="legend-stack"
                    direction="row"
                    spacing={2}
                    sx={{
                        borderLeft: `1px solid ${theme.palette.divider}`,
                        pl: 0,
                        ml: 0,
                        maxWidth: 0,
                        opacity: 0,
                        overflow: 'hidden',
                        transition: 'all 0.5s cubic-bezier(0.4, 0, 0.2, 1)',
                        whiteSpace: 'nowrap'
                    }}
                >
                    <Stack direction="row" alignItems="center" spacing={0.5} sx={{ ml: 2 }}>
                        <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: 'success.main' }} />
                        <Typography variant="caption" sx={{ fontWeight: 700, fontSize: '0.7rem' }}>
                            Success
                        </Typography>
                    </Stack>
                    <Stack direction="row" alignItems="center" spacing={0.5}>
                        <Box
                            sx={{
                                width: 8,
                                height: 8,
                                borderRadius: '50%',
                                bgcolor: 'success.main',
                                animation: `${pulse} 2s infinite ease-in-out`
                            }}
                        />
                        <Typography variant="caption" sx={{ fontWeight: 700, fontSize: '0.7rem' }}>
                            Success (Retried)
                        </Typography>
                    </Stack>
                    <Stack direction="row" alignItems="center" spacing={0.5}>
                        <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: 'primary.main' }} />
                        <Typography variant="caption" sx={{ fontWeight: 700, fontSize: '0.7rem' }}>
                            Running
                        </Typography>
                    </Stack>
                    <Stack direction="row" alignItems="center" spacing={0.5}>
                        <Box
                            sx={{
                                width: 8,
                                height: 8,
                                borderRadius: '50%',
                                bgcolor: 'primary.main',
                                border: `1px solid ${theme.palette.common.white}`
                            }}
                        />
                        <Typography variant="caption" sx={{ fontWeight: 700, fontSize: '0.7rem' }}>
                            Delay/Timer
                        </Typography>
                    </Stack>
                    <Stack direction="row" alignItems="center" spacing={0.5}>
                        <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: 'warning.main' }} />
                        <Typography variant="caption" sx={{ fontWeight: 700, fontSize: '0.7rem' }}>
                            Waiting Webhook
                        </Typography>
                    </Stack>
                    <Stack direction="row" alignItems="center" spacing={0.5}>
                        <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: 'error.main' }} />
                        <Typography variant="caption" sx={{ fontWeight: 700, fontSize: '0.7rem' }}>
                            Failed
                        </Typography>
                    </Stack>
                </Stack>
            </Box>
        </>
    );

    const FlowControls = () => {
        const { fitView, zoomIn, zoomOut } = useReactFlow();
        return (
            <Box
                sx={{
                    position: 'absolute',
                    bottom: 20,
                    left: 20,
                    zIndex: 10,
                    bgcolor: alpha(theme.palette.background.paper, 0.8),
                    backdropFilter: 'blur(10px)',
                    p: '6px 12px',
                    borderRadius: '50px',
                    border: `1px solid ${alpha(theme.palette.divider, 0.3)}`,
                    display: 'flex',
                    alignItems: 'center',
                    gap: 0.5,
                    boxShadow: '0 4px 12px rgba(0,0,0,0.1)'
                }}
            >
                <Tooltip title="Acercar">
                    <IconButton
                        onClick={() => zoomIn()}
                        size="small"
                        sx={{
                            color: theme.palette.primary.main,
                            '&:hover': { bgcolor: alpha(theme.palette.primary.main, 0.1) }
                        }}
                    >
                        <AddIcon fontSize="small" />
                    </IconButton>
                </Tooltip>
                <Tooltip title="Alejar">
                    <IconButton
                        onClick={() => zoomOut()}
                        size="small"
                        sx={{
                            color: theme.palette.primary.main,
                            '&:hover': { bgcolor: alpha(theme.palette.primary.main, 0.1) }
                        }}
                    >
                        <RemoveIcon fontSize="small" />
                    </IconButton>
                </Tooltip>
                <Box sx={{ width: '1px', height: '16px', bgcolor: theme.palette.divider, mx: 0.5 }} />
                <Tooltip title="Centrar y ajustar flujo">
                    <IconButton
                        onClick={() => fitView({ duration: 800, padding: 0.3 })}
                        size="small"
                        sx={{
                            color: theme.palette.secondary.main,
                            '&:hover': { bgcolor: alpha(theme.palette.secondary.main, 0.1) }
                        }}
                    >
                        <FilterCenterFocusIcon fontSize="small" />
                    </IconButton>
                </Tooltip>
                <Tooltip title={isFullScreen ? 'Salir de pantalla completa' : 'Ver en pantalla completa'}>
                    <IconButton
                        onClick={toggleFullScreen}
                        size="small"
                        sx={{
                            color: theme.palette.primary.main,
                            '&:hover': { bgcolor: alpha(theme.palette.primary.main, 0.1) }
                        }}
                    >
                        {isFullScreen ? <FullscreenExitIcon fontSize="small" /> : <FullscreenIcon fontSize="small" />}
                    </IconButton>
                </Tooltip>
            </Box>
        );
    };

    return (
        <Box
            sx={{
                bgcolor: finalBg,
                borderRadius: 0,
                height: isFullScreen ? '100vh' : 435,
                width: isFullScreen ? '100vw' : '100%',
                position: isFullScreen ? 'fixed' : 'relative',
                top: isFullScreen ? 0 : 'auto',
                left: isFullScreen ? 0 : 'auto',
                zIndex: isFullScreen ? 9999 : 1,
                overflow: 'hidden',
                border: isFullScreen ? 'none' : `1px solid ${alpha(theme.palette.divider, 0.1)}`,
                boxShadow: `inset 0 0 50px ${alpha(theme.palette.common.black, 0.1)}`,
                mb: isFullScreen ? 0 : 3,
                transition: 'all 0.3s ease'
            }}
        >
            <ReactFlowProvider>
                <FlowHeader />
                <FlowControls />
                <ReactFlow
                    nodes={nodes}
                    edges={edges}
                    onNodesChange={onNodesChange}
                    onEdgesChange={onEdgesChange}
                    nodeTypes={nodeTypes}
                    fitView
                    fitViewOptions={{ padding: 0.3 }}
                    minZoom={0.2}
                    maxZoom={1.5}
                    proOptions={{ hideAttribution: true }}
                >
                    <GraphViewportManager currentNodes={nodes} historyData={executionHistoryData} />
                    <Background
                        color={theme.palette.mode === 'dark' ? alpha(theme.palette.common.white, 0.1) : '#e2e8f0'}
                        gap={30}
                        size={2}
                        variant={BackgroundVariant.Dots}
                    />
                    <MiniMap
                        nodeColor={(n: any) => n.data.color}
                        maskColor={alpha(theme.palette.background.paper, 0.3)}
                        style={{
                            borderRadius: 16,
                            border: `1px solid ${alpha(theme.palette.divider, 0.2)}`,
                            bottom: 20,
                            right: 20,
                            width: 150,
                            height: 100,
                            background: alpha(theme.palette.background.paper, 0.8),
                            backdropFilter: 'blur(8px)'
                        }}
                    />
                </ReactFlow>
            </ReactFlowProvider>
        </Box>
    );
};

export default StateMachineFlowGraph;
